import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import css from "./EmptyState.module.css";

export interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  /**
   * Optional primary call-to-action: a link somewhere the user can fix the
   * emptiness, or — when the emptiness is a failed load (ADR 318) — a handler
   * that retries it in place. Same affordance either way.
   */
  action?: { to: string; label: string } | { onClick: () => void; label: string };
}

/**
 * A calm, centered "there's nothing here yet — here's what to do" surface
 * (ADR 175). Replaces silently rendering a full view against no data (e.g. the
 * demo 3D box on a warehouse-less system, which read as a broken model).
 */
export function EmptyState({ icon, title, description, action }: EmptyStateProps) {
  return (
    <div className={css.wrap} role="status">
      {icon && <div className={css.icon} aria-hidden="true">{icon}</div>}
      <h2 className={css.title}>{title}</h2>
      {description && <p className={css.desc}>{description}</p>}
      {action && ("to" in action
        ? <Link to={action.to} className={css.cta}>{action.label}</Link>
        : <button type="button" className={css.cta} onClick={action.onClick}>{action.label}</button>)}
    </div>
  );
}
